/**
 * "How it's going" (mockups 53–55): the grown-ups' dashboard
 * ([D-194](../../../docs/DECISIONS.md#d-194)).
 *
 * Three tabs over one family's rollups: which **lessons** children leave and
 * where, how much they **remember** at review against the 0.92 aim, and where
 * their **acorns** come from and go. Everyone summed, or one child — never
 * two side by side, because a page that compares siblings is a leaderboard
 * ([D-015](../../../docs/DECISIONS.md#d-015)).
 *
 * The same component is a child's own page about themselves, opened from
 * Settings: anything a grown-up can see about a child, the child can see too
 * ([D-039](../../../docs/DECISIONS.md#d-039)). Only `load` differs.
 *
 * Read-only. The charts are hand-drawn SVG, so the page adds no dependency
 * ([Telemetry](../../../docs/TELEMETRY.md#implementation-notes)).
 */

import type { Dashboard as Page, DashboardDays } from '@capytalk/core';
import {
  type KeyboardEvent,
  type PointerEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import '@capytalk/tokens/blocks/dashboard.css';

type Tab = 'Lessons' | 'Learning' | 'Acorns';
const TABS: readonly Tab[] = ['Lessons', 'Learning', 'Acorns'];
const DAYS: readonly DashboardDays[] = [7, 30, 90];
/** The most points a line draws a dot on each of. */
const DOTS = 14;
/** How many lessons the table shows before "Show all". */
const FIRST_LESSONS = 25;

/** What the ledger's sources are called on this page. */
const SOURCE: Readonly<Record<string, string>> = {
  lesson: 'Lessons',
  chapter: 'Chapters',
  quest: 'Quests',
  quests: 'Quests',
  chest: 'Chests',
  streak: 'Streak',
  milestone: 'Milestones',
  achievement: 'Badges',
  bonus: 'Bonus rounds',
  practice: 'Practice',
  review: 'Review',
  story: 'Stories',
  comeback: 'Coming back',
  shop: 'Shop',
  dye: 'Dyes',
  admin: 'From a grown-up',
};

const capital = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);
const sourceName = (source: string): string => SOURCE[source] ?? capital(source);
const percent = (share: number | null): string =>
  share === null ? '–' : `${String(Math.round(share * 100))}%`;
const count = (n: number): string => n.toLocaleString('en-GB');
/** `2026-09-20` as "20 Sept", read as a UTC day so no timezone moves it. */
const dayName = (day: string): string =>
  new Date(`${day}T00:00:00.000Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

/** A round step for an axis: 1, 2 or 5 times a power of ten. */
function niceStep(rough: number): number {
  if (rough <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(rough));
  const unit = rough / power;
  return (unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 5 ? 5 : 10) * power;
}

interface Point {
  readonly value: number | null;
  /** What the tooltip says under the value. */
  readonly detail: string;
}

/**
 * One series, one axis: a 2px line, ringed end-dots, recessive hairlines.
 *
 * The hover layer is a crosshair that snaps to the nearest point, and the
 * same readout follows the arrow keys once the chart has focus. Every value is
 * also in a table a screen reader can read, so the tooltip never gates one.
 */
function LineChart({
  label,
  points,
  lo,
  hi,
  ticks,
  format,
  aim,
}: {
  readonly label: string;
  readonly points: readonly Point[];
  readonly lo: number;
  readonly hi: number;
  readonly ticks: readonly number[];
  readonly format: (v: number) => string;
  readonly aim?: number;
}) {
  const [at, setAt] = useState<number | null>(null);
  const box = useRef<SVGSVGElement>(null);
  const [w, h, l, r, t, b] = [340, 150, 34, 10, 10, 22];
  const x = (i: number) => l + ((w - l - r) * i) / Math.max(1, points.length - 1);
  const y = (v: number) => t + (h - t - b) * (1 - (v - lo) / (hi - lo));
  const drawn = points.flatMap((p, i) => (p.value === null ? [] : [{ i, v: p.value }]));
  const path = drawn
    .map(({ i, v }, n) => `${n === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`)
    .join(' ');

  const nearest = (event: PointerEvent<SVGSVGElement>) => {
    const rect = box.current?.getBoundingClientRect();
    if (rect === undefined || rect.width === 0) return;
    const across = ((event.clientX - rect.left) / rect.width) * w;
    const step = (w - l - r) / Math.max(1, points.length - 1);
    setAt(Math.min(points.length - 1, Math.max(0, Math.round((across - l) / step))));
  };
  const keys = (event: KeyboardEvent<SVGSVGElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const move = event.key === 'ArrowLeft' ? -1 : 1;
    setAt((now) =>
      Math.min(points.length - 1, Math.max(0, (now ?? points.length - 1) + move)),
    );
  };
  const shown = at === null ? undefined : points[at];

  return (
    <div className="dchart">
      <svg
        ref={box}
        viewBox={`0 0 ${String(w)} ${String(h)}`}
        role="img"
        aria-label={label}
        tabIndex={0}
        onPointerMove={nearest}
        onPointerLeave={() => {
          setAt(null);
        }}
        onFocus={() => {
          setAt(points.length - 1);
        }}
        onBlur={() => {
          setAt(null);
        }}
        onKeyDown={keys}
      >
        {ticks.map((v) => (
          <g key={v}>
            <line
              x1={l}
              x2={w - r}
              y1={y(v)}
              y2={y(v)}
              stroke="#EFEAE0"
              strokeWidth={1}
            />
            <text x={l - 6} y={y(v) + 4} textAnchor="end" fontSize={10} fill="#776F62">
              {format(v)}
            </text>
          </g>
        ))}
        {aim !== undefined && (
          <g>
            <line
              x1={l}
              x2={w - r}
              y1={y(aim)}
              y2={y(aim)}
              stroke="#7C7466"
              strokeWidth={1}
            />
            <text x={l + 4} y={y(aim) - 4} fontSize={10} fill="#776F62">
              aim {format(aim)}
            </text>
          </g>
        )}
        {at !== null && (
          <line
            x1={x(at)}
            x2={x(at)}
            y1={t}
            y2={h - b}
            stroke="#C6BDAD"
            strokeWidth={1}
          />
        )}
        <path
          d={path}
          fill="none"
          stroke="#43569A"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {/* A dot on every point while there are few; past that, only the
            newest, and whichever one the crosshair is on. */}
        {drawn
          .filter(
            ({ i }) => points.length <= DOTS || i === at || i === points.length - 1,
          )
          .map(({ i, v }) => (
            <circle
              key={i}
              cx={x(i)}
              cy={y(v)}
              r={4}
              fill="#43569A"
              stroke="#fff"
              strokeWidth={2}
            />
          ))}
      </svg>
      {shown !== undefined && at !== null && (
        <div
          className="dtip"
          style={{
            // Kept inside the card at both ends, and below the line, where it
            // covers the empty half of the plot rather than the data.
            left: `${String(Math.min(78, Math.max(22, (x(at) / w) * 100)))}%`,
            top: '58%',
          }}
          aria-hidden="true"
        >
          <b>{shown.value === null ? 'Nothing yet' : format(shown.value)}</b>
          {shown.detail}
        </div>
      )}
      <table className="sronly">
        <caption>{label}</caption>
        <tbody>
          {points.map((p, i) => (
            <tr key={i}>
              <td>{p.detail}</td>
              <td>{p.value === null ? 'nothing yet' : format(p.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One measure, one colour: bars from a shared baseline, the value at the tip. */
function Bars({
  rows,
  colour,
}: {
  readonly rows: readonly { readonly name: string; readonly value: number }[];
  readonly colour: string;
}) {
  const top = Math.max(1, ...rows.map((row) => row.value));
  return (
    <div className="dbars">
      {rows.map((row) => (
        <div className="dbar" key={row.name}>
          <span>{row.name}</span>
          <div className="bt">
            <div
              className="bf"
              style={{
                width: `${String((100 * row.value) / top)}%`,
                background: colour,
              }}
            />
          </div>
          <span className="bv">{count(row.value)}</span>
        </div>
      ))}
    </div>
  );
}

function Lessons({ page }: { readonly page: Page }) {
  const [all, setAll] = useState(false);
  const shown = all ? page.lessons : page.lessons.slice(0, FIRST_LESSONS);
  return (
    <>
      <div className="card" style={{ marginTop: 12, padding: '12px 10px' }}>
        <h3 style={{ margin: '0 4px 8px' }}>Lessons, the ones to look at first</h3>
        {page.lessons.length === 0 ? (
          <p className="sub" style={{ margin: '0 4px' }}>
            No lessons played in these days.
          </p>
        ) : (
          <table className="dtable">
            <thead>
              <tr>
                <th>Lesson</th>
                <th>Started</th>
                <th>Left</th>
                <th>Right</th>
                <th>Left at</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((lesson) => (
                <tr key={lesson.lessonId}>
                  <td>
                    {lesson.label}
                    {lesson.worry && (
                      <>
                        <br />
                        <span className="worry">Worth a look</span>
                      </>
                    )}
                  </td>
                  <td>{lesson.starts}</td>
                  <td>{lesson.abandons}</td>
                  <td>{percent(lesson.accuracy)}</td>
                  <td>{lesson.leftAtMost ?? '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!all && page.lessons.length > FIRST_LESSONS && (
          <button
            className="chip"
            type="button"
            style={{ margin: '8px 4px 0' }}
            onClick={() => {
              setAll(true);
            }}
          >
            Show all {page.lessons.length}
          </button>
        )}
        <div className="tiny" style={{ margin: '8px 4px 0' }}>
          &ldquo;Left at&rdquo; is the question most often left on. Worth a look: left
          more than once in three, or under 60% right, after at least three goes.
        </div>
      </div>
      <div className="card" style={{ marginTop: 10 }}>
        <h3>Things that broke</h3>
        {page.errors.length === 0 ? (
          <p className="sub" style={{ marginTop: 4 }}>
            Nothing broke.
          </p>
        ) : (
          page.errors.map((error) => (
            <div className="lrow" key={`${error.code} ${error.screen}`}>
              <div className="ic" aria-hidden="true">
                🔧
              </div>
              <div className="tx">
                On the {error.screen} screen
                <div className="tiny">
                  {error.code} · {error.n === 1 ? 'once' : `${String(error.n)} times`}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}

function Learning({
  page,
  days,
}: {
  readonly page: Page;
  readonly days: DashboardDays;
}) {
  const learning = page.learning;
  const values = learning.recallOverTime.flatMap((p) =>
    p.recall === null ? [] : [p.recall],
  );
  // From 50% unless a point fell below it: the interesting band is near the
  // aim, and a line needs no zero the way a bar does.
  const lo = Math.min(0.5, Math.floor(Math.min(1, ...values) * 10) / 10);
  // A week is drawn a day at a time; anything longer a week at a time, as the
  // core buckets it.
  const each = days === 7 ? 'day by day' : 'week by week';
  return (
    <>
      <div className="dtiles">
        <div className="dtile">
          <div className="tl">Remembered at review</div>
          <div className="tv">{percent(learning.recall)}</div>
          <div className="td">
            The aim is {percent(learning.target)} · {count(learning.reviews)} reviews
          </div>
        </div>
        <div className="dtile">
          <div className="tl">Lessons finished</div>
          <div className="tv">{count(learning.lessonsDone)}</div>
          <div className="td">{count(learning.answers)} answers</div>
        </div>
        <div className="dtile">
          <div className="tl">Level moved up</div>
          <div className="tv">{learning.bandUp}</div>
          <div className="td">When it was too easy</div>
        </div>
        <div className="dtile">
          <div className="tl">Level moved down</div>
          <div className="tv">{learning.bandDown}</div>
          <div className="td">When it was too hard</div>
        </div>
      </div>
      {learning.reviews === 0 ? (
        <div className="card" style={{ marginTop: 10 }}>
          <h3>Remembered at review</h3>
          <p className="sub" style={{ marginTop: 4 }}>
            Nothing came up for review in these days. Words come back a day or more
            after they are first met.
          </p>
        </div>
      ) : (
        <div className="card" style={{ marginTop: 10 }}>
          <h3>Remembered at review, {each}</h3>
          <LineChart
            label={`Remembered at review, ${each}`}
            points={learning.recallOverTime.map((p) => ({
              value: p.recall,
              detail: `${each === 'day by day' ? '' : 'week of '}${dayName(p.from)} · ${count(p.reviews)} reviews`,
            }))}
            lo={lo}
            hi={1}
            ticks={[lo, (lo + 1) / 2, 1]}
            format={(v) => percent(v)}
            aim={learning.target}
          />
          <div className="tiny" style={{ marginTop: 6 }}>
            Words seen on an earlier day and answered right. Below the aim, reviews come
            sooner; above it, later.
          </div>
        </div>
      )}
    </>
  );
}

function Acorns({ page, days }: { readonly page: Page; readonly days: DashboardDays }) {
  const acorns = page.acorns;
  const change = acorns.end - acorns.start;
  // Round ticks, about three of them, and a top that clears the highest day.
  const most = Math.max(1, ...acorns.balance.map((b) => b.balance));
  // Never less than one: there is no such thing as half an acorn.
  const step = Math.max(1, niceStep(most / 3));
  const hi = step * Math.ceil(most / step);
  const earned = acorns.sources
    .filter((s) => s.earned > 0)
    .map((s) => ({ name: sourceName(s.source), value: s.earned }));
  const spent = acorns.sources
    .filter((s) => s.spent > 0)
    .map((s) => ({ name: sourceName(s.source), value: s.spent }));
  return (
    <>
      <div className="dtiles">
        <div className="dtile">
          <div className="tl">Acorns now</div>
          <div className="tv">{count(acorns.end)}</div>
          <div className="td">
            {change === 0
              ? `The same as ${String(days)} days ago`
              : `${count(Math.abs(change))} ${change > 0 ? 'more' : 'fewer'} than ${String(days)} days ago`}
          </div>
        </div>
        <div className="dtile">
          <div className="tl">Wardrobe owned</div>
          <div className="tv">{percent(acorns.ownedShare)}</div>
          <div className="td">Each child, on average</div>
        </div>
      </div>
      <div className="card" style={{ marginTop: 10 }}>
        <h3>Acorns, day by day</h3>
        <LineChart
          label="Acorns, day by day"
          points={acorns.balance.map((b) => ({
            value: b.balance,
            detail: dayName(b.day),
          }))}
          lo={0}
          hi={hi}
          ticks={Array.from({ length: Math.round(hi / step) + 1 }, (_, i) => i * step)}
          format={count}
        />
      </div>
      <div className="card" style={{ marginTop: 10 }}>
        <h3>Where acorns came from</h3>
        {earned.length === 0 ? (
          <p className="sub">None earned in these days.</p>
        ) : (
          <Bars rows={earned} colour="#44833B" />
        )}
        <h3 style={{ marginTop: 12 }}>What they were spent on</h3>
        {spent.length === 0 ? (
          <p className="sub">None spent in these days.</p>
        ) : (
          <Bars rows={spent} colour="#E8843C" />
        )}
      </div>
      <div className="card" style={{ marginTop: 10, padding: '12px 10px' }}>
        <h3 style={{ margin: '0 4px 6px' }}>
          Chests, against the odds children are shown
        </h3>
        <table className="dtable">
          <thead>
            <tr>
              <th>Chest</th>
              <th>Opened</th>
              <th>Clothes</th>
              <th>Got</th>
              <th>Promised</th>
            </tr>
          </thead>
          <tbody>
            {acorns.chests.map((c) => (
              <tr key={c.tier}>
                <td>{capital(c.tier)}</td>
                <td>{c.opened}</td>
                <td>{c.withItem}</td>
                <td>{percent(c.actual)}</td>
                <td>{percent(c.published)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="tiny" style={{ margin: '8px 4px 0' }}>
          A few chests wander a long way from the odds. After 15 without clothes the
          next always has some, so real rates run a little above them.
        </div>
      </div>
    </>
  );
}

export default function Dashboard({
  title,
  intro,
  children = [],
  load,
  onClose,
}: {
  readonly title: string;
  /** A line under the title, for the child's own page. */
  readonly intro?: string;
  /** The grown-up's children, for the picker. Empty on a child's own page. */
  readonly children?: readonly {
    readonly learnerId: string;
    readonly displayName: string;
  }[];
  readonly load: (days: DashboardDays, learnerId: string | null) => Promise<Page>;
  readonly onClose: () => void;
}) {
  const [days, setDays] = useState<DashboardDays>(30);
  const [who, setWho] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('Lessons');
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    load(days, who)
      .then((next) => {
        if (!live) return;
        setPage(next);
        setFailed(false);
      })
      .catch(() => {
        if (live) setFailed(true);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [load, days, who]);

  return (
    <div className="grow pad" style={{ overflowY: 'auto', paddingBottom: 24 }}>
      <div className="row" style={{ marginTop: 8 }}>
        <button
          className="chip"
          type="button"
          aria-label="Close"
          onClick={onClose}
          style={{ padding: '6px 10px' }}
        >
          ✕
        </button>
        <h2 style={{ flex: 1 }}>{title}</h2>
      </div>
      {intro !== undefined && (
        <p className="sub" style={{ marginTop: 6 }}>
          {intro}
        </p>
      )}

      {children.length > 1 && (
        <div className="dfilters" role="group" aria-label="Whose learning">
          {[{ learnerId: null, displayName: 'Everyone' }, ...children].map((child) => (
            <button
              key={child.learnerId ?? 'everyone'}
              className={`chip${who === child.learnerId ? ' on' : ''}`}
              type="button"
              aria-pressed={who === child.learnerId}
              onClick={() => {
                setWho(child.learnerId);
              }}
            >
              {child.displayName}
            </button>
          ))}
        </div>
      )}
      <div className="dfilters" role="group" aria-label="How far back">
        {DAYS.map((n) => (
          <button
            key={n}
            className={`chip${days === n ? ' on' : ''}`}
            type="button"
            aria-pressed={days === n}
            onClick={() => {
              setDays(n);
            }}
          >
            {n} days
          </button>
        ))}
      </div>
      <div className="dtabs" role="tablist">
        {TABS.map((name) => (
          <button
            key={name}
            className={tab === name ? 'on' : ''}
            type="button"
            role="tab"
            aria-selected={tab === name}
            onClick={() => {
              setTab(name);
            }}
          >
            {name}
          </button>
        ))}
      </div>

      {failed && page === null && (
        <p className="sub" role="alert" style={{ marginTop: 12 }}>
          That did not load. Try again in a moment.
        </p>
      )}
      {page !== null && (
        // While a new window loads, the old one stays at half strength: no
        // flash, no jump, and nothing that looks like the new numbers.
        <div role="tabpanel" aria-busy={loading} style={{ opacity: loading ? 0.5 : 1 }}>
          <div className="tiny" style={{ marginTop: 8 }}>
            {dayName(page.window.from)} – {dayName(page.window.to)}. Today is added
            tonight.
          </div>
          {tab === 'Lessons' && <Lessons page={page} />}
          {tab === 'Learning' && <Learning page={page} days={days} />}
          {tab === 'Acorns' && <Acorns page={page} days={days} />}
        </div>
      )}
    </div>
  );
}
